import { FC, useState } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useWallet } from '@solana/wallet-adapter-react'
import { TransferTokenButton } from '@/components/organisms/TransferTokenButton'
import { getQueryValue, sanitizeUrl, useStores } from '../../../utils'
import styles from './index.module.scss'
import { ProfileContactButton } from '../ProfileContactButton'
import {
  IPublicUser,
  MessageType,
  TokenTransactionCategoryBase,
  UserProfileAllowMessageRequestsFrom,
} from '../../../types'
import { IconButton } from '../../atoms/IconButton'
import MoreIconSvg from '../../../public/images/common/more_icon.svg'
import NotificationPlusIconSvg from '../../../public/images/common/notification_plus_icon.svg'
import { Button } from '../../atoms/Button'
import LinkIconSvg from '../../../public/images/common/link_icon.svg'
import ReportIconSvg from '../../../public/images/common/report_icon.svg'
import { OutSideOverlay } from '../../atoms/OutSideOverlay'
import { UserNotificationSettingsModal } from '../UserNotificationSettingsModal'

type Props = {
  user: IPublicUser
}

export const UserDetailShareAndReportContent: FC<Props> = observer(({ user }) => {
  const { t } = useTranslation(['p'])
  const { viewer, messages } = useStores()
  const router = useRouter()
  const isMyProfilePage = getQueryValue('id', router) === viewer.viewer.username
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showNotificationMenu, setShowNotificationMenu] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { connected } = useWallet()

  const onClickMore = (): void => {
    setShowMoreMenu(!showMoreMenu)
  }

  const onClickNotification = (): void => {
    setShowNotificationMenu(!showNotificationMenu)
  }

  const copy = async () => {
    const link = `${process.env.NEXT_PUBLIC_APP_BASE_URL}/u/${user?.username}`
    const isSuccessful = await navigator.clipboard.writeText(link).then(
      () => {
        return true
      },
      () => {
        return false
      }
    )
    const messageText = isSuccessful ? t('コピーしました！') : t('コピーに失敗しました')

    messages.add({
      type: MessageType.Info,
      body: messageText,
      isTranslated: true,
      ttl: 5000,
    })

    onClickMore()
  }

  let contactIsDisable = false
  if (user?.profile.allowMessageRequestsFrom === UserProfileAllowMessageRequestsFrom.EVERYONE) {
    contactIsDisable = false
  } else if (user?.isFollower) {
    contactIsDisable = false
  } else {
    contactIsDisable = true
  }

  const toggleFollow = async () => {
    if (viewer.isSignedIn && !isSubmitting) {
      setIsSubmitting(true)
      const newUser = await viewer.viewer?.toggleFollowUser(user.username)
      if (newUser) {
        user.update(newUser)
      }
      setIsSubmitting(false)
    }
  }

  const isSendabled = viewer.viewer.profile?.avatar?.length > 0 && viewer.viewer.profile?.bio?.length > 0

  const addTransaction = async (amount: string, signature: string): Promise<boolean> => {
    const output = await viewer.viewer?.addTokenTransaction({
      tokenTransaction: {
        amount,
        category: TokenTransactionCategoryBase.PROFILE,
        recieverId: user?.id,
        signature,
      },
    })

    if (output.isSuccessful) {
      return true
    }
    return false
  }

  return (
    <div className={styles.container}>
      {/* TODO: クリック後のウィンドウ */}
      <div className={styles.moreIcon}>
        <IconButton componentType='button' onClick={onClickMore}>
          <MoreIconSvg />
        </IconButton>

        {showMoreMenu && (
          <>
            <OutSideOverlay onClick={onClickMore} isOpen={showMoreMenu} backGroundColor='transparent' />
            <div className={styles.moreMenuContainer}>
              <div className={styles.moreMenuItem} onClick={copy} onKeyPress={copy} role='button' tabIndex={0}>
                <div className={styles.moreMenuIcon}>
                  <LinkIconSvg />
                </div>
                {t('プロフィールのリンクをコピー')}
              </div>
              <a
                className={styles.moreMenuItem}
                href={sanitizeUrl(process.env.NEXT_PUBLIC_SUPPORT_FORM_URL)}
                target='_blank'
                rel='noreferrer'
                onClick={onClickMore}
              >
                <div className={styles.moreMenuIcon}>
                  <ReportIconSvg />
                </div>
                {t('報告する')}
              </a>
            </div>
          </>
        )}
      </div>
      {user.profile?.solanaAddress && viewer.isSignedIn && connected && (
        <div className={styles.icon}>
          <TransferTokenButton user={user} addTransaction={addTransaction} />
        </div>
      )}
      {!contactIsDisable && !isMyProfilePage && (
        <div className={styles.icon}>
          <ProfileContactButton
            href={`/messages/?with=${user?.username}`}
            isDisabled={false}
            isSendabled={isSendabled}
          />
        </div>
      )}
      {viewer.isSignedIn && user?.isFollowing && !isMyProfilePage && (
        <>
          <div className={styles.icon}>
            <IconButton
              componentType='button'
              disabled={!(viewer.isSignedIn && user?.isFollowing && !isMyProfilePage)}
              onClick={onClickNotification}
            >
              <NotificationPlusIconSvg />
            </IconButton>
          </div>
          {showNotificationMenu && (
            <>
              <OutSideOverlay onClick={onClickNotification} isOpen={showNotificationMenu} backGroundColor='gray' />
              <UserNotificationSettingsModal user={user} toggleModal={onClickNotification} />
            </>
          )}
        </>
      )}
      {isMyProfilePage && (
        <div>
          <Button href='/account/profile' styleType='outline' color='lightGray' componentType='a' buttonSize='small'>
            {t('プロフィール編集')}
          </Button>
        </div>
      )}
      {!isMyProfilePage && (
        <div>
          {user?.isFollowing ? (
            <Button
              styleType='outline'
              color='lightGray'
              componentType='button'
              buttonSize='small'
              disabled={!(viewer.isSignedIn && !isSubmitting)}
              onClick={toggleFollow}
              isLoading={isSubmitting}
            >
              {t('フォロー中')}
            </Button>
          ) : (
            <Button
              styleType='default'
              color='blue'
              componentType='button'
              buttonSize='small'
              disabled={!(viewer.isSignedIn && !isSubmitting)}
              onClick={toggleFollow}
              isLoading={isSubmitting}
            >
              {t('フォロー')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
})
